import { genId } from "./ids";

// Traduce entre el objeto `torneo` en memoria (claves en inglés, ids de cliente)
// y el documento del backend (claves en español, refs ObjectId).

export const isObjectId = (v) => typeof v === "string" && /^[0-9a-fA-F]{24}$/.test(v);

// El walk-over viaja en español en el backend y en inglés en el front, igual que
// los equipos del partido (local/visitante <-> home/away). "ambos"/"both" es el
// WO de los dos equipos.
const WO_AL_SERVIDOR = { home: "local", away: "visitante", both: "ambos" };
const WO_AL_FRONT = { local: "home", visitante: "away", ambos: "both" };
const woAlServidor = (wo) => WO_AL_SERVIDOR[wo] || "";
const woAlFront = (wo) => WO_AL_FRONT[wo] || "";

const toMinuto = (v) => (v === "" || v == null || Number.isNaN(Number(v)) ? null : Number(v));

const golAlServidor = (g) => ({
  jugador: isObjectId(g.playerId) ? g.playerId : null,
  minuto: toMinuto(g.minute),
});

const playerRefId = (p) => {
  const candidate = p._id || p.value || p.id;
  return isObjectId(candidate) ? candidate : null;
};

// El _id que puso Mongo en el subdocumento. Se conserva en los dos sentidos del
// mapeo para que rueda/zona/fecha/partido tengan una identidad estable entre dos
// lecturas del torneo: los ids de cliente (genId) se regeneran en cada mapeo y no
// sirven para nombrarle un partido al backend (ver PATCH /torneos/:id/partido).
const conServerId = (obj, source) => {
  if (source && source._id) obj._id = String(source._id);
  return obj;
};

// Un partido tal como lo espera el backend. Vive aparte de mapToServerModel porque
// el guardado de un resultado manda un solo partido, no el torneo entero.
export function partidoAlServidor(m) {
  return conServerId(
    {
      local: isObjectId(m.homeTeamId) ? m.homeTeamId : null,
      visitante: isObjectId(m.awayTeamId) ? m.awayTeamId : null,
      hora: m.hora || "",
      cancha: m.cancha ? Number(m.cancha) : null,
      jugado: !!m.jugado,
      wo: woAlServidor(m.wo),
      golesLocal: (m.homeGoals || []).map(golAlServidor),
      golesVisitante: (m.awayGoals || []).map(golAlServidor),
    },
    m
  );
}

// Solo el resultado: lo único que la pantalla de resultados puede cambiar de un
// partido. Los equipos, la hora y la cancha se editan en el ABM del torneo.
export function resultadoAlServidor(m) {
  return {
    jugado: !!m.jugado,
    wo: woAlServidor(m.wo),
    golesLocal: (m.homeGoals || []).map(golAlServidor),
    golesVisitante: (m.awayGoals || []).map(golAlServidor),
  };
}

// ---- Piezas sueltas, para los guardados granulares ----
// Todo lo que cuelga de una fecha se guarda de a un pedazo (ver torneosService.js y
// server routes/torneos.js), así que además del torneo entero hay que saber armar
// cada pedazo por separado.

// El día y la sede de una fecha.
export function cabeceraAlServidor(d) {
  return { fecha: d.date || "", sede: isObjectId(d.sede) ? d.sede : null };
}

// Qué cambió entre el partido que había y el editado, con las claves del backend.
// Se manda sólo eso, y no el partido entero, para no pisar con una copia vieja lo
// que otro dispositivo esté cargando del mismo partido: cambiar el equipo limpia
// los goles y el WO, pero corregir la hora no los tiene por qué tocar.
export function cambiosDelPartido(anterior, actualizado) {
  const antes = partidoAlServidor(anterior);
  const ahora = partidoAlServidor(actualizado);
  const cambios = {};
  Object.keys(ahora).forEach((campo) => {
    // El _id nombra al partido, no se edita; `jugado` es del resultado y el ABM no
    // lo toca.
    if (campo === "_id" || campo === "jugado") return;
    if (JSON.stringify(antes[campo]) !== JSON.stringify(ahora[campo])) cambios[campo] = ahora[campo];
  });
  return cambios;
}

// ---- Front -> Backend ----
export function mapToServerModel(torneo) {
  const payload = {
    nombre: torneo.nombre,
    ano: Number(torneo.ano) || undefined,
    equipos: (torneo.equipos || []).map((t) => ({
      equipo: isObjectId(t.id) ? t.id : null,
      nombre: t.name,
      baja: !!t.baja,
      // Solo la referencia al jugador: el nombre y la camiseta salen del padrón.
      // Los jugadores sin ref real (agregados a mano y no vinculados) se omiten.
      listaFeTorneo: (t.players || [])
        .filter((p) => playerRefId(p))
        .map((p) => ({ jugador: playerRefId(p) })),
    })),
    ruedas: (torneo.ruedas || []).map((r) =>
      conServerId(
        {
          nombre: r.name,
          zonas: (r.zones || []).map((z) =>
            conServerId(
              {
                nombre: z.name,
                equipos: (z.teamIds || []).filter(isObjectId),
                fechas: (z.matchDates || []).map((d) =>
                  conServerId(
                    {
                      fecha: d.date || "",
                      sede: isObjectId(d.sede) ? d.sede : null,
                      listaFeFecha: Object.entries(d.attendance || {})
                        .filter(([equipoId]) => isObjectId(equipoId))
                        .map(([equipoId, jugadorIds]) => ({
                          equipo: equipoId,
                          jugadores: (jugadorIds || []).filter(isObjectId),
                        })),
                      partidos: (d.matches || []).map(partidoAlServidor),
                    },
                    d
                  )
                ),
              },
              z
            )
          ),
        },
        r
      )
    ),
  };
  if (torneo._id) payload._id = torneo._id;
  return payload;
}

// "Apellido, Nombre" — el mismo rótulo con el que el padrón lista a los jugadores.
const nombreDeJugador = (j) => [j.apellido, j.nombre].filter(Boolean).join(", ");

// ---- Backend -> Front ----
// Regenera ids de cliente frescos; los ObjectIds (equipos/jugadores/sede) se
// preservan como claves estables. player.id === jugadorObjectId re-alinea attendance.
//
// El `record` tiene que venir con la lista de fe populada (POPULATE_ROSTER en el
// backend): el roster guarda solo la ref al jugador, así que el nombre y la
// camiseta salen del padrón, no del torneo.
export function mapToViewModel(record) {
  const mapGol = (g) => ({
    id: genId("goal"),
    playerId: g.jugador ? String(g.jugador) : "",
    minute: g.minuto == null ? "" : String(g.minuto),
  });

  const mapPartido = (p) =>
    conServerId(
      {
        id: genId("match"),
        homeTeamId: p.local ? String(p.local) : "",
        awayTeamId: p.visitante ? String(p.visitante) : "",
        hora: p.hora || "",
        cancha: p.cancha != null ? String(p.cancha) : "",
        jugado: !!p.jugado,
        wo: woAlFront(p.wo),
        homeGoals: (p.golesLocal || []).map(mapGol),
        awayGoals: (p.golesVisitante || []).map(mapGol),
      },
      p
    );

  const mapFecha = (f) =>
    conServerId(
      {
        id: genId("date"),
        date: f.fecha || "",
        sede: f.sede ? String(f.sede) : "",
        attendance: Object.fromEntries(
          (f.listaFeFecha || []).map((a) => [String(a.equipo), (a.jugadores || []).map(String)])
        ),
        matches: (f.partidos || []).map(mapPartido),
      },
      f
    );

  return {
    _id: record._id,
    nombre: record.nombre || "",
    ano: record.ano || new Date().getFullYear(),
    equipos: (record.equipos || []).map((e) => ({
      _id: String(e.equipo),
      id: String(e.equipo),
      name: e.nombre,
      baja: !!e.baja,
      // Un renglón cuyo jugador ya no está en el padrón (lo borraron) no tiene
      // nada que mostrar: se descarta.
      players: (e.listaFeTorneo || [])
        .map((r) => r.jugador)
        .filter((j) => j && j._id)
        .map((j) => ({
          id: String(j._id),
          _id: String(j._id),
          value: String(j._id),
          name: nombreDeJugador(j),
          camiseta: j.camiseta || "",
          isExcepcion: !!j.isExcepcion,
        })),
    })),
    ruedas: (record.ruedas || []).map((ru) =>
      conServerId(
        {
          id: genId("round"),
          name: ru.nombre,
          zones: (ru.zonas || []).map((z) =>
            conServerId(
              {
                id: genId("zone"),
                name: z.nombre,
                teamIds: (z.equipos || []).map(String),
                matchDates: (z.fechas || []).map(mapFecha),
              },
              z
            )
          ),
        },
        ru
      )
    ),
  };
}
